import React, { useCallback, useEffect, useRef, useState } from "react";
import { SvgV2 } from "@whatslly/ui-components/v2/BaseElements/SvgV2";
import styles from "./audio-player.module.scss";
import classnames from "classnames";

const addHeadingZero = (num: number): string => {
  return num > 9 ? num.toString() : `0${num}`;
};

const getDisplayTimeBySeconds = (seconds: number): string | undefined => {
  const min = Math.floor(seconds / 60);
  const minStr = addHeadingZero(min);
  const secStr = addHeadingZero(Math.floor(seconds % 60));

  return `${minStr}:${secStr}`;
};
const AudioControls: React.FC<{
  isPlaying: boolean;
  onPlayPauseClick: (isPlaying: boolean) => void;
}> = ({ isPlaying, onPlayPauseClick }) => (
  <span className={styles.audioControls}>
    {isPlaying ? (
      <SvgV2 onClick={() => onPlayPauseClick(false)} width={19} height={14} iconName={`pause`} />
    ) : (
      <SvgV2 onClick={() => onPlayPauseClick(true)} width={19} height={19} iconName={`play`} />
    )}
  </span>
);

export const AudioPlayer: React.FC<{ audioSrc: string; className?: string }> = ({ audioSrc, className }) => {
  const [trackProgress, setTrackProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [durationMetadata, setDurationMetadata] = useState(NaN);

  const audioRef = useRef(new Audio(audioSrc));
  const intervalRef = useRef<any>(undefined);

  const startTimer = () => {
    clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => {
      if (audioRef.current.ended) {
        setTrackProgress(0);
        audioRef.current.pause();
        setIsPlaying(false);
        clearInterval(intervalRef.current);
      } else {
        setTrackProgress(audioRef.current.currentTime);
      }
    }, 500);
  };

  const onScrub = (value: number) => {
    clearInterval(intervalRef.current);
    audioRef.current.currentTime = value;
    setTrackProgress(audioRef.current.currentTime);
  };

  const onScrubEnd = () => {
    if (!isPlaying) {
      onPlayPauseClick(true);
    }
    startTimer();
  };

  const onPlayPauseClick = useCallback((isPlaying: boolean) => {
    setIsPlaying(isPlaying);

    if (isPlaying) {
      audioRef.current.play();
      startTimer();
    } else {
      audioRef.current.pause();
    }
  }, []);

  const getAudioTime = () => {
    if (isPlaying) {
      return getDisplayTimeBySeconds(trackProgress);
    }
    if (Number.isNaN(audioRef.current.duration)) {
      return "--:--";
    }
    return getDisplayTimeBySeconds(audioRef.current.duration);
  };

  useEffect(() => {
    function onLoadMetadata() {
      if (audioRef.current.duration === Infinity || isNaN(Number(audioRef.current.duration))) {
        audioRef.current.currentTime = 1e101;
        audioRef.current.addEventListener("timeupdate", getDuration);
      }
      setDurationMetadata(audioRef.current.duration);
    }
    function getDuration() {
      setDurationMetadata(audioRef.current.duration);
      audioRef.current.currentTime = 0;
      audioRef.current.removeEventListener("timeupdate", getDuration);
    }

    audioRef.current.addEventListener("loadedmetadata", onLoadMetadata);
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.removeEventListener("timeupdate", getDuration);
        audioRef.current.removeEventListener("loadedmetadata", onLoadMetadata);
      }

      clearInterval(intervalRef.current);
    };
  }, []);

  return (
    <div className={classnames(styles.audioPlayer, className)}>
      <div className={styles.audioPlayerTop}>
        <AudioControls isPlaying={isPlaying} onPlayPauseClick={onPlayPauseClick} />

        <input
          type="range"
          value={trackProgress}
          step="1"
          min="0"
          max={durationMetadata ? durationMetadata : `${durationMetadata}`}
          className="progress"
          onChange={(e) => onScrub(Number(e.target.value))}
          onMouseUp={onScrubEnd}
          onKeyUp={onScrubEnd}
        />
      </div>

      <div className={styles.audioTime}>{getAudioTime()}</div>
    </div>
  );
};
